<template>
  <div v-if="canUse" class="ask-mm">
    <!-- Floating launcher -->
    <button v-if="!open" class="ask-mm__fab" @click="open = true" title="Ask MediaMachine">
      <b-icon icon="robot-happy-outline" size="is-small" />
      <span>Ask MediaMachine</span>
    </button>

    <!-- Panel -->
    <div v-else class="ask-mm__panel">
      <div class="ask-mm__head">
        <span><b-icon icon="robot-happy-outline" size="is-small" /> Ask MediaMachine</span>
        <div class="ask-mm__head-actions">
          <button v-if="turns.length" class="ask-mm__clear" @click="clear" title="Clear chat">Clear</button>
          <button class="ask-mm__close" @click="close" aria-label="Close">&times;</button>
        </div>
      </div>

      <div ref="body" class="ask-mm__body">
        <div v-if="scope && (scope.tenant || scope.organisation_id)" class="ask-mm__scope">
          Answering for: <strong>{{ scope.tenant || 'this tenant' }}</strong>
          <span v-if="scope.organisation_id" class="ask-mm__scope-id">· {{ String(scope.organisation_id).slice(0, 8) }}</span>
        </div>
        <div v-if="!turns.length" class="ask-mm__hint">
          Ask about spot processing, the Traffic Agent, scheduling, playout or feeds.
          I answer from the BMS engineering log + live status. Read-only — I can't change anything.
        </div>
        <div v-for="(t, i) in turns" :key="i" :class="['ask-mm__msg', 'is-' + t.role]">
          <div class="ask-mm__bubble">{{ t.content }}</div>
          <div v-if="t.sources && t.sources.length" class="ask-mm__sources">
            sources: {{ t.sources.join(', ') }}
          </div>
        </div>
        <div v-if="loading" class="ask-mm__msg is-assistant">
          <div class="ask-mm__bubble ask-mm__typing">thinking…</div>
        </div>
      </div>

      <form class="ask-mm__input" @submit.prevent="send">
        <input
          v-model="question"
          type="text"
          placeholder="Ask a question…"
          :disabled="loading"
          autocomplete="off"
        />
        <button type="submit" :disabled="loading || !question.trim()">Send</button>
      </form>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ctoApi from '@/api/cto'

// Internal Contech staff (superadmin always allowed). Staff log into each tenant with a
// sub-addressed email <name>+<tenant>@contechmedia.com — match on the base name (before "+")
// so every staff×tenant login is covered. Mirrors the backend; backend enforces authoritatively.
const STAFF = ['christie', 'nakedi', 'dion', 'lesley', 'portia', 'reabetswe', 'francois']
const DOMAIN = 'contechmedia.com'

function isInternalStaffEmail(email) {
  if (!email) return false
  const e = String(email).trim().toLowerCase()
  const at = e.indexOf('@')
  if (at < 0) return false
  if (e.slice(at + 1) !== DOMAIN) return false
  const base = e.slice(0, at).split('+')[0]
  return STAFF.includes(base)
}

export default {
  name: 'AskMediaMachine',
  data() {
    return {
      open: false,
      question: '',
      turns: [],
      loading: false,
      scope: null
    }
  },
  computed: {
    ...mapGetters(['user']),
    canUse() {
      const u = this.user
      if (!u) return false
      const isSuper = u.role && u.role.includes && u.role.includes('superadmin')
      return !!isSuper || isInternalStaffEmail(u.email)
    },
    // Changes whenever the logged-in user / tenant changes.
    identityKey() {
      const u = this.user || {}
      return (u.email || '') + '|' + (u.organisation_id || '') + '|' + (u.id || '')
    }
  },
  watch: {
    identityKey() {
      // Login or tenant switched — reset so there is no cross-tenant chat carryover.
      this.clear()
      this.open = false
    }
  },
  methods: {
    async send() {
      const q = this.question.trim()
      if (!q || this.loading) return
      this.turns.push({ role: 'user', content: q })
      this.question = ''
      this.loading = true
      const history = this.turns
        .slice(0, -1)
        .map(t => ({ role: t.role, content: t.content }))
      try {
        const res = await ctoApi.ask(q, history)
        this.scope = res.scope || this.scope
        this.turns.push({
          role: 'assistant',
          content: res.response || '(no response)',
          sources: res.sources || []
        })
      } catch (err) {
        const msg =
          (err && err.data && (err.data.error || err.data.details)) ||
          'Request failed. Check your connection or ask Francois.'
        this.turns.push({ role: 'assistant', content: '⚠️ ' + msg, sources: [] })
      } finally {
        this.loading = false
        this.$nextTick(this.scrollToBottom)
      }
    },
    scrollToBottom() {
      const el = this.$refs.body
      if (el) el.scrollTop = el.scrollHeight
    },
    clear() {
      this.turns = []
      this.question = ''
      this.scope = null
    },
    close() {
      this.open = false
      this.clear() // reset when closed — each open starts fresh
    }
  }
}
</script>

<style scoped>
.ask-mm { position: fixed; right: 20px; bottom: 20px; z-index: 1000; font-size: 14px; }

.ask-mm__fab {
  display: flex; align-items: center; gap: 6px;
  background: #363636; color: #fff; border: none; border-radius: 24px;
  padding: 10px 16px; cursor: pointer; box-shadow: 0 2px 10px rgba(0,0,0,0.25);
}
.ask-mm__fab:hover { background: #00d1b2; }

.ask-mm__panel {
  width: 370px; max-width: calc(100vw - 40px);
  height: 520px; max-height: calc(100vh - 40px);
  background: #fff; border-radius: 10px; overflow: hidden;
  box-shadow: 0 6px 30px rgba(0,0,0,0.3); display: flex; flex-direction: column;
}

.ask-mm__head {
  background: #363636; color: #fff; padding: 10px 14px;
  display: flex; align-items: center; justify-content: space-between;
}
.ask-mm__close { background: none; border: none; color: #fff; font-size: 22px; line-height: 1; cursor: pointer; }
.ask-mm__head-actions { display: flex; align-items: center; gap: 10px; }
.ask-mm__clear { background: none; border: 1px solid rgba(255,255,255,0.4); color: #fff; border-radius: 4px; font-size: 12px; padding: 2px 8px; cursor: pointer; }
.ask-mm__clear:hover { background: rgba(255,255,255,0.15); }

.ask-mm__body { flex: 1; overflow-y: auto; padding: 12px; background: #f9f9f9; }
.ask-mm__hint { color: #7a7a7a; font-size: 13px; line-height: 1.4; }
.ask-mm__scope { background: #eef9f7; border: 1px solid #b8e6dd; color: #22726a; font-size: 12px; padding: 5px 8px; border-radius: 6px; margin-bottom: 10px; }
.ask-mm__scope-id { color: #9a9a9a; }

.ask-mm__msg { margin-bottom: 10px; display: flex; flex-direction: column; }
.ask-mm__msg.is-user { align-items: flex-end; }
.ask-mm__msg.is-assistant { align-items: flex-start; }

.ask-mm__bubble {
  max-width: 85%; padding: 8px 11px; border-radius: 10px;
  white-space: pre-wrap; word-wrap: break-word; line-height: 1.4; color: #363636;
}
.is-user .ask-mm__bubble { background: #00d1b2; color: #fff; }
.is-assistant .ask-mm__bubble { background: #fff; border: 1px solid #e5e5e5; }
.ask-mm__typing { color: #7a7a7a; font-style: italic; }
.ask-mm__sources { font-size: 11px; color: #9a9a9a; margin-top: 3px; max-width: 85%; }

.ask-mm__input { display: flex; border-top: 1px solid #e5e5e5; }
.ask-mm__input input { flex: 1; border: none; padding: 12px; outline: none; font-size: 14px; }
.ask-mm__input button {
  border: none; background: #00d1b2; color: #fff; padding: 0 16px; cursor: pointer; font-weight: 600;
}
.ask-mm__input button:disabled { background: #b5b5b5; cursor: not-allowed; }
</style>
